import { action, computed, flow, observable, makeObservable } from "mobx"
import { TraxisCpeIdMixin } from "store/api/mixin/TraxisCpeIdMixin"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { alias, serializable, deserialize, primitive, list } from "serializr"
import { getQlHeaders } from "store/qlapi/QlHeaders"
import { urlFixHttpProtocolMismatch } from "store/util/urlFixHttpProtocolMismatch"
import { appFeatures } from "AppFeatures"

import { getCoCoModule } from "store/page/repo/CoCoModule"
const CoCo = await getCoCoModule()

// MTVW-999: not used anymore, but kept for documentation reference
class TvChannel {
	@serializable id = null
	@serializable(alias("name", primitive())) Name = null
	@serializable(list()) packageIds = null
	@serializable(alias("logoUri", primitive())) logoUrl = null
	@serializable(alias("lcn", primitive())) LogicalChannelNumber = null
	@serializable(alias("isAdult", primitive())) IsAdult = null
	@serializable isEncrypted = null
	/* MTVW-1043: Eliminate rollingBufferId
	// The rollingBufferId is used to create a replay streaming session. For further information refer to maxReplayDuration.
	// If an empty string is given, replay and recording is not allowed at all on this channel.
	@serializable rollingBufferId = null
	*/

	// The maximal allowed replay duration in ISO 8601 format. Usually set to P7D, P1DT6H, PT2H.
	// If set to PT0S, no replay is allowed at all. This field never contains an empty string.
	// The actual replay duration depends also on the recording opt-in date of the user.
	// ```
	// if isSubscribed is false:
	//   replay is not allowed.
	// else if maxReplayDuration is PT0S:
	//   replay is not allowed.
	// else if recordingAndReplayAllowedTill <= now
	//   replay is not allowed.
	// else if recordingAndReplayAllowedSince == 0:
	//   replay is allowed since now-PT2H.
	// else if now-maxReplayDuration < recordingAndReplayAllowedSince:
	//   replay is allowed since recordingAndReplayAllowedSince.
	// else:
	//   replay is allwed since now-maxReplayDuration.
	// ...
	@serializable maxReplayDuration = null
	// If set to true, it is allowed to book recordings on this channel. Set to false if no recordings may be booked.
	// ```
	// if isSubscribed is false:
	//   booking of recordings is not allowed.
	// else if maxReplayDuration is PT0S or isRecordingAllowed is false:
	//   booking of recordings is not allowed.
	// else if recordingAndReplayAllowedTill <= now
	//   booking of recordings is not allowed.
	// else if recordingAndReplayAllowedSince == 0:
	//   booking of recordings is allowed since now-PT2H.
	// else if now-maxReplayDuration < recordingAndReplayAllowedSince:
	//   booking of recordings is allowed since recordingAndReplayAllowedSince.
	// else:
	//   booking of recordings is allwed since now-maxReplayDuration.
	// ```
	@serializable hasNPVR = null // isRecordingAllowed
	// This property restricts the time from which content can be recorded resp. played via replay. In millis since epoch.
	// This is also known as Recording-OptIn date. If the customer did not opt-in to this channel, yet, 0 will be returned.
	@serializable recordingAndReplayAllowedSince = null
	// This property restricts the time from which recording of content resp. replay playback is no longer allowed.
	// In millis since epoch. This value is usually set if the customer booked a DayPass, to watch a channel for a single day.
	// The value defines the end of this 24h period. As soon as the 24h period has passed, the customer is no longer allowed
	// to watch replay resp. to book recordings, since he has not subscribed the channel. For regularly subscribed channels
	// a date in the future will be returned.
	@serializable recordingAndReplayAllowedTill = null
	// True if the customer subscribed this channel and can play its content. False otherwise.
	@serializable isSubscribed = null
	// True if the channel is part of Branchen Vereinbarung. The clients need to call adsMS.
	@serializable hasAlternativeAds = null
	// True if the channel is part of Branchen Vereinbarung. The clients need to call adsMS.
	@serializable hasMarkers = null
	// True if the channel is part of Branchen Vereinbarung and user can skip ads. True for premium subscription only.
	@serializable hasAdSkipping = null
	// True if the channel has replay. Set to false if no replay content is available.
	// ```
	// if isSubscribed is false:
	//   set to false
	// else if user cannot fast forward or rewind:
	//   set to false
	// else if maxReplayDuration <= PT2H:
	//   set to false
	// else if recordingAndReplayAllowedSince == 0:
	//   set to false
	// else:
	//   set to true
	// ```
	@serializable hasReplay = null
	// True if the channel allows livePause.
	// ```
	// if isSubscribed is false:
	//   set to false
	// else if maxReplayDuration = PT0H:
	//   set to false
	// else:
	//   set to true
	// ```
	@serializable hasLivePause = null
	// True if the channel allows start from the beginning.
	// ```
	// if isSubscribed is false:
	//   set to false
	// else if maxReplayDuration = PT0H:
	//   set to false
	// else:
	//   set to true
	// ```
	@serializable hasStartOver = null
	// True if client can position within the content.
	// ```
	// if isSubscribed is false:
	// 	set to false
	// else if can_fast_forward from cookie is false:
	// 	set to false
	// else if maxReplayDuration = PT0H:
	// 	set to false
	// else:
	// 	set to true
	// ```
	@serializable hasForwardRewind = null
	// True if channel is meant to be displayed only on set top box
	@serializable isSTBOnly = null
	// True if channel supports recordings.
	// The field is based only on the channel property, it doesn't include user subscription. For that see hasNPVR.
	// For presenting channels for opt -in show all channels with isReplayChannel = true or isNPVRChannel = true.
	@serializable isNPVRChannel = null
	//  True if channel has 7 days rolling buffer.
	// The field is 'false' for channels supporting startOver only(2h rolling buffer).See isStartOverChannel.
	// The field is based only on the channel property, it doesn't include user subscription. For that see hasReplay.
	// For presenting channels for opt -in show all channels with isReplayChannel = true or isNpvrChannel = true.
	@serializable isReplayChannel = null
	// True if the channel has at least 2h of rolling buffer.
	// The field is based only on the channel property, it doesn't include user subscription. For that see hasStartOver.
	@serializable isStartOverChannel = null
	/* MTVW-464: No HD badge
	// enum: [ SD, HD, UHD ]
	@serializable resolution = null // deprecated replacement for IsHd
	*/
	// unused replay_into_past (i.e. ReplayIntoPast) returned in EpgService
	// TODO: Needed? Not returned by MS
	IsOnRecordingOptinList = true

	/* MTVW-464: No HD badge
	get IsHD() {
		return this.resolution === "HD"
	}
	*/
}

const STD_HEADERS = {
	'Accept': 'application/json',
	// Would cause CORS in keepAlive
	//'pragma': 'no-cache', 'cache-control': 'no-cache'
}
export class ChannelsService extends TraxisCpeIdMixin {
	activeChannelId = null
	activeChannelListId = null
	_activeChannels = []
	_channelsIncludingAdult = []
	_channelsExcludingAdult = []
	_channelLists = []
	_allChannelLists = []
	_channelList = {}
	adultChannels = []
	notSubscribedChannels = []
	stbChannels = []

	constructor() {
		super(parent)
		makeObservable(this, {
			activeChannelId: observable,
			activeChannelListId: observable,
			_activeChannels: observable.shallow,
			_channelLists: observable.shallow,
			_channelList: observable, // MTVW-1056: don't use shallow to allow reassign of CoCo object
			_allChannelLists: observable,
			channels: computed,
			channelLists: computed,
			channelList: computed,
			getChannelsAsync: action,
			getChannelListAsync: action,
			setActiveChannel: action,
			setActiveChannelListId: action
		})
	}

	init = flow(function* () {
		yield this.getChannelsAsync(appFeatures.showAdultContent)
		yield this.getChannelListsAsync(true)
		this.setActiveChannel(this.getFirstChannelListChannel)
   })

	get channels() {
		return this._activeChannels
	}

	get channelLists() {
		return this._channelLists
	}

	get channelList() {
		return this._channelList
	}

	// MTVW-859
	setActiveChannel(channelId) {
		this.activeChannelId = channelId
	}

	setActiveChannelListId(chListId) {
		this.activeChannelListId = chListId
	}

	get getFirstChannel() {
		return this._activeChannels?.[0]
	}

	get getFirstChannelListChannel() {
		console.debug("getFirstChannelListChannel", this._channelList?.channels?.[0])
		return this._channelList?.channels?.[0]
	}

	getChannel(channelId) {
		const index = this._activeChannels?.findIndex(i => i.id === channelId)
		return (index > -1) ? this._activeChannels?.[index] : null
	}

	// MTVW-1094
	isChannelPinProtected(channelId) {
		const channel = this.getChannel(channelId)
		//console.debug("isChannelPinProtected", channelId, channel)
		return channel?.isAdult && appFeatures.isAdultContentProtected
	}

	// MTVW-1094
	isChannelSubscribed(channelId) {
		const channel = this.getChannel(channelId)
		//console.debug("isChannelSubscribed", channelId, channel)
		return channel?.isSubscribed
	}

	isAdultChannel(channelId) {
		const channel = this.getChannel(channelId)
		let result = channel?.isAdult
		// if appFeatures.showAdultContent = false, adult channels are filtered out and channel is null 
		if (!channel && !appFeatures.showAdultContent) result = true
		//console.debug("isAdultChannel", channelId, channel)
		return result
	}

	indexInChannelList(channelId) {
		return this._channelList?.channels?.indexOf(channelId)
	}

	indexInChannelLists(channelLists, channelListId) {
		return channelLists?.findIndex(i => i.id === channelListId)
	}

	getChannelName(channelId) {
		const channel = this.getChannel(channelId)
		if (!channel) return null
		return channel.Name
	}

	// MTVW-728
	getChannelsArrayAsync = flow(function* (includeAdultChannels) {
		const channels = yield this.getChannelsAsync(includeAdultChannels)
		const channelsArray = []
		channels?.forEach(i => {
			channelsArray.push(i.id)
		})
		return channelsArray
	})

	getChannelsFromList(list) {
		//if (this.channels.length === 0) console.warning("getChannelsFromList", list, this.channels.length, this.channels)
		if (!list) {
			//console.debug("getChannelsFromList ALL")
			return this._activeChannels
		}
		else {
			return this.channelIdsToChannels(list.channels)
		}
	}

	// added for traxis cleanup
	getChannelsForList = flow(function* (id = null) {
		if (id === null) {
			//return this._activeChannels
			return []
		}

		const chList = yield this.getChannelListAsync(id)
		//return (!chList?.Channels) ? [] : chList.Channels.Channel?.map(i => self.Channels.find(elem => elem.id === i.id)).filter(self._isChannelFilter)
		return this.getChannelsFromList(chList)
	})

	channelIdsToChannels(channelIds) {
		const result = []
		channelIds?.forEach(i => {
			const local = this._activeChannels.find((j => j.id === i))
			if (local) result.push(local)
		})
		//console.debug("channelIdsToChannels %o, result %o", channelIds, result)
		return result
	}

	preloadChannelPictures(param) {
		return this._channelsIncludingAdult?.map(i => {
			if (i.logoUrl !== null) {
				let img = new Image()
				img.src = i.logoUrl + param
				img.onerror = (e) => { /*console.debug("logo not found %s", i.logoUrl)*/ }
				//console.debug("IMG %s", i.logoUrl + param)
				return img
			}
			return null
		})
	}

	_resetChannelFilterStats() {
		this.adultChannels = []
		this.notSubscribedChannels = []
		this.stbChannels = []
	}

	_channelFilter(includeAdultChannels, i) {
		//console.debug("_channelFilter i=%o, adult=%s", i, includeAdultChannels)
		if (!i) {
			return false
		}
		if (i.IsAdult) {
			// MTVW-1094: support for adult channels
			this.adultChannels.push(i)
			return includeAdultChannels
		}
		if (!i.isSubscribed) {
			this.notSubscribedChannels.push(i)
			return false
		}
		// MTVW-503: UHD channel filter, decision changed to use backend solution
		/*
		if (i.isSTBOnly) {
			this.stbChannels.push(i)
			return false
		}
		*/
		return true
	}

	_applyChannelFilters(msChannels, includeAdultChannels) {
		let filtered = []
		// MTVW-312: if condition for robustness
		if (Array.isArray(msChannels)) {
			this._resetChannelFilterStats()
			filtered = msChannels.filter(this._channelFilter.bind(this, includeAdultChannels))
			console.debug("total ms channels %s, filtered %s, adult %s, %o, not subscribed %s, %o, stb %s", msChannels?.length, filtered?.length, this.adultChannels?.length, this.adultChannels, this.notSubscribedChannels?.length, this.notSubscribedChannels, this.stbChannels?.length)
			//console.debug("total ms channels %s, filtered %s, adult %s, channels %o, not subscribed %s, channels %o, stb %s, channels %o", msChannels?.length, filtered?.length, this.adultChannels?.length, this.adultChannels, this.notSubscribedChannels?.length, this.notSubscribedChannels, this.stbChannels?.length, this.stbChannels)
		}
		return filtered
	}

	get xHeaders() {
		// make a copy of STD_HEADERS, otherwise STD_HEADERS would be changed!
		const headers = {}
		Object.assign(headers, STD_HEADERS)
		Object.assign(headers, getQlHeaders())
		return headers
	}

	version = flow(function* () {
		let resultJson = yield CoCo.apiFetchChannelsVersionRaw()
		// CoCo returns string instead of object, need to convert to object
		if (typeof resultJson === "string") resultJson = JSON.parse(resultJson)
		return `${resultJson["build_tag"]} (${resultJson["build_date"]})`
	})

	versionUrl() {
		return [serviceUrls.channelsUrl + "version", this.xHeaders]
	}

	getChannelsAsync = flow(function* (includeAdultChannels = false) {
		//if (!contractId) return []
		//console.trace("getChannelsAsync", includeAdultChannels)
		if (includeAdultChannels && this._channelsIncludingAdult?.length > 0) {
			this._activeChannels = this._channelsIncludingAdult
			return this._channelsIncludingAdult
		}
		else if (!includeAdultChannels && this._channelsExcludingAdult?.length > 0) {
			this._activeChannels = this._channelsExcludingAdult
			return this._channelsExcludingAdult
		}
		try {
			// NOTE that cocoDiscover.discover() fetches channels twice (independent of the passed FetchStrategy)
			let cocoResult = yield CoCo.fetchChannels(CoCo.FetchStrategy.CacheFirst)
			if (!cocoResult) return []
			// CoCo returns string instead of object, need to convert to object
			if (typeof cocoResult === "string") cocoResult = JSON.parse(cocoResult)

			// We don't have all the information in the channels to do the filtering and ordering of channels
			// (e.g.non subscribed channels). Therefore we still need to use the traxis ChannelListRepo

			// MTVW-1056: TODO, remove upper case once channel list editor uses CoCo
			cocoResult?.forEach(i => {
				// MTVW-999: MS impl. used logoUrl, CoCo uses logoUri
				if (i.logoUri) {
					i.logoUri = urlFixHttpProtocolMismatch(i.logoUri)
					i.logoUrl = i.logoUri
				}
				// MTVW-999: MS impl. upper case, CoCo lower case
				if (i.name) i.Name = i.name
				if (i.isAdult) i.IsAdult = i.isAdult
				if (i.lcn) i.LogicalChannelNumber = i.lcn
				//i.isPlayable only in cocoChannels
			})
			//console.debug("CHANNELS %o, ALL %o", this.channels)
			// change this._channelsIncludingAdult once we have the channel list with filters applied
			this._channelsIncludingAdult = this._applyChannelFilters(cocoResult, true)
			// MTVW-831: "?mode=box&w=" + 44 * 4 -> "?mode=box&w=480&h=270"
			this.preloadChannelPictures("?mode=box&w=480&h=270")
			console.debug("CoCo.fetchChannels", this._channelsIncludingAdult)
			for (let i = 0; i < this._channelsIncludingAdult.length; i++) {
				if (this._channelsIncludingAdult[i].recordingAndReplayAllowedSince === 0) console.debug("NO opt-in", this._channelsIncludingAdult[i].Name, this._channelsIncludingAdult[i].recordingAndReplayAllowedSince, this._channelsIncludingAdult[i])
				if (!this._channelsIncludingAdult[i].hasReplay) console.debug("NO replay", this._channelsIncludingAdult[i].Name, this._channelsIncludingAdult[i].recordingAndReplayAllowedSince, this._channelsIncludingAdult[i])
			}
			this._channelsExcludingAdult = this._applyChannelFilters(cocoResult, false)
			this._activeChannels = includeAdultChannels ? this._channelsIncludingAdult : this._channelsExcludingAdult
		}
		catch (e) {
			console.error("CoCo.fetchChannels", e)
			return []
		}

		/*
		for (let i = 0; i < radioChannels.channels.length; i++)
			console.debug("radio", radioChannels.channels[i].streamUri)
		*/

		return this._activeChannels
	})

	getRadioChannelsAsync = flow(function* () {
		const radioChannels = yield CoCo.radioRaw(CoCo.FetchStrategy.CacheFirst)
		//console.debug("radio", radioChannels)
		/*
		for (let i = 0; i < radioChannels.channels.length; i++)
			console.debug("radio", radioChannels.channels[i].streamUri)
		*/
		return radioChannels
	})

	// MTVW-1044: check for subscribed channels in channel list
	getSubscribedChannelsInChannelList(chList) {
		const channels = []
		for (let i = 0; i < chList.channels.length; i++) {
			if (this.getChannel(chList.channels[i])) channels.push(chList.channels[i])
		}
		return channels
	}

	// MTVW-1013:
	getChannelListsAsync = flow(function* (reload = false) {
		// MTVW-1013: TODO	let channelLists = []
		//console.debug("CoCo.fetchChannelLists calling")
		try {
			// MTVW-1098: make sure that all channel lists for the current profile are private
			this._allChannelLists = yield this.getAllChannelListsAsync()
			// MTVW-1044: ChannelListMode.Edit to include ALL predefined channel lists
			let chLists = yield CoCo.fetchChannelLists(CoCo.ChannelListMode.Edit, reload ? CoCo.FetchStrategy.NetworkFirst : CoCo.FetchStrategy.CacheFirst)
			//console.debug("CoCo.fetchChannelLists", chLists)
			if (!chLists) return this._channelLists
			// MTVW-313, TODO: move to ChannelListEditor.validateChannelListName once change in CoCo has been applied
			const isUnique = yield this.validateChannelListNameAsync("Senderliste")
			// MTVW-1044: check for subscribed channels in channel list
			for (let i = 0; i < chLists.length; i++) {
				if (this.getSubscribedChannelsInChannelList(chLists[i])?.length === 0) {
					//console.debug("channelList empty", chLists[i])
					chLists.splice(i, 1)
					i--
				}
			}
			let channelListId = rootStore.sso.browserStore.getChannelListId()
			if (this.indexInChannelLists(chLists, channelListId) < 0) {
				// fallback
				channelListId = chLists[0].id
				rootStore.sso.browserStore.setChannelList(channelListId, chLists[0].name)
			}
			const chList = yield this.getChannelListAsync(channelListId ?? chLists[0].id, reload)
			//this.setActiveChannel(chList.channels[0])
			for (let i = 0; i < chLists.length; i++) {
				if (chLists[i].type === CoCo.ChannelListType.Predefined) {
					chLists[i].firstPredefined = true
					break
				}
			}
			this._channelLists = chLists
			return chLists
		}
		catch (e) {
			console.debug("CoCo.fetchChannelLists", e)
			return this._channelLists
		}
	})

	// MTVW-1098: make personal channel lists public
	getAllChannelListsAsync = flow(function* () {
		const allChannelLists = yield CoCo.apiFetchAllChannelLists()
		allChannelLists.ChannelLists?.ChannelList?.forEach(item => {
			//console.debug("CoCo.channelList=", item.Name, item.ProfileId, item.IsPersonal, item.Channels.Channel.length)
			try {
				// MTVW-1098: make personal channel lists public
				if (item.IsPersonal && rootStore?.sso?.profile?.id === item.ProfileId) CoCo.apiMakeChannelListPublicRaw(item.id)
			}
			catch (e) {
				console.error("CoCo.apiMakeChannelListPublicRaw", e)
			}
		})
		return allChannelLists
	})

	getAllChannelLists() {
		return this._allChannelLists
	}

	// MTVW-1013:
	getChannelListAsync = flow(function* (id, reload = false) {
		let channelLists = []
		try {
			// MTVW-1044: ChannelListMode.Edit to include ALL predefined channel lists
			let chList = yield CoCo.fetchChannelList(id, CoCo.ChannelListMode.Edit, reload ? CoCo.FetchStrategy.NetworkFirst : CoCo.FetchStrategy.CacheFirst)
			if (!chList) this._channelList
			// CoCo returns string instead of object, need to convert to object
			//if (typeof chList === "string") chList = JSON.parse(chList)
			// MTVW-1056: we cannot splice CoCo's chList.channels, use a temporary array copy
			const channels = chList.channels.slice()
			console.debug("CoCo.fetchChannelList", chList.name)
			// MTVW-1041: exclude unsubscribed channels
			for (let i = 0; i < channels.length; i++) {
				if (!this.getChannel(channels[i])) {
					//console.debug("exclude", chList.channels[i])
					channels.splice(i, 1)
					i--
				}
			}
			chList.channels = channels
			this._channelList = chList
			return chList
		}
		catch (e) {
			console.error("CoCo.fetchChannelList", e)
			return this._channelList
		}
	})

	deleteChannelListAsync = flow(function* (id) {
		yield CoCo.deleteChannelList(id)
	})

	createChannelListAsync = flow(function* (name) {
		const result = yield CoCo.createChannelList(name)
		// MTVW-1098: default is already public
		//yield CoCo.apiMakeChannelListPublicRaw(result.id)
		return result
	})

	updateChannelListAsync = flow(function* (channelListId, name, channels, isPublic = true) {
		return yield CoCo.updateChannelList(channelListId, name, channels /*, isPublic*/)
	})

	validateChannelListNameAsync = flow(function* (name) {
		return yield CoCo.validateChannelListName(name)
	})

	// MTVW-728
	replayOptIn = flow(function* (contractId, channels) {
		const result = yield CoCo.apiOptInReplay(channels)
		return result
	})

	// MTVW-728: It clears the optIn date. Requires Admin authorization.
	// It's not intended to be used by the client. Only for test automation purposes
	/*
	replayOptOut = flow(function* (contractId) {
		const result = yield CoCo.apiOptOutReplay()
		return result
	})
	*/
}
